import { useEffect, useMemo, useState } from 'react';
import { ChartCardProps } from '@intelligence/pages/MarketProfile/components/ChartCard/types';
import {
  getMobilePageData,
  IMarketProfileMobilePageData,
} from '@intelligence/services/marketProfile';
import { METRICS } from '../constants';
import moment from 'moment';
import { getCountryByName, TCountryInfo } from '@intelligence/pages/MarketProfile/_util';
import useDeepCompareMemo from '@intelligence/hooks/useDeepCompareMemo';
import { useDeepCompareEffect } from 'ahooks';

export type TChartType = ChartCardProps['chartType'];
export type TMainSegment = ChartCardProps['mainSegment'];

export interface InputItem {
  date: string;
  market: string;
  metric: 'revenue' | 'download';
  source: string;
  value: number;
  value_ratio: number;
}

export interface OutputData {
  [countryKey: string]: {
    revenue: (number | string)[];
    download: (number | string)[];
  };

  xAxis: string[];
}

const transformData = ({
  input,
  countryMap,
}: {
  input: InputItem[];
  countryMap: TCountryInfo[];
}): OutputData => {
  // 生成连续月份序列
  const months = input
    .map((item) => moment(item.date).startOf('month').format('YYYY-MM'))
    .sort((a, b) => a.localeCompare(b));

  const minMonth = months[0];
  const maxMonth = months[months.length - 1];

  const xAxis: string[] = [];
  for (
    let current = moment(minMonth);
    current.isSameOrBefore(maxMonth, 'month');
    current.add(1, 'month')
  ) {
    xAxis.push(current.format('YYYY-MM'));
  }

  // 动态构建国家数据集
  const countryData = new Map<
    string,
    {
      revenue: (number | string)[];
      download: (number | string)[];
    }
  >();

  // 创建索引映射
  const monthIndexMap = new Map(xAxis.map((month, idx) => [month, idx]));

  input.forEach((item) => {
    // 通过market代码获取国家信息
    const country = getCountryByName({
      countryMap,
      abbr: item.market, // 根据market代码查询
    });

    if (!country) return;

    if (!countryData.has(country.country_name_en)) {
      countryData.set(country.country_name_en, {
        revenue: new Array(xAxis.length).fill('-'),
        download: new Array(xAxis.length).fill('-'),
      });
    }

    const monthKey = moment(item.date).startOf('month').format('YYYY-MM');
    const idx = monthIndexMap.get(monthKey);

    if (typeof idx === 'number') {
      const target = countryData.get(country.country_name_en)!;
      target[item.metric][idx] = item.value;
    }
  });

  return {
    xAxis,
    ...Object.fromEntries(countryData),
  };
};

interface IUseChartConfigProps {
  selectedList: any[];
  fetchParams: IMarketProfileMobilePageData;
  dataSource: string;
  countryMap: TCountryInfo[];
}

export default function useChartConfig(props: IUseChartConfigProps) {
  const { selectedList, fetchParams, dataSource, countryMap } = props;
  const [chartType, setChartType] = useState<TChartType>('chart-map');
  const [mainSegment, setMainSegment] = useState<TMainSegment>('Revenue');
  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [chartData, setChartData] = useState<any>([]);

  const updateParams = useDeepCompareMemo(() => {
    if (!selectedList.length || !fetchParams.start_date || !fetchParams.end_date) return null;

    const regions = selectedList.map((item) => {
      return item.market;
    });

    return {
      // ...fetchParams,
      source_metrics: { [dataSource]: ['revenue', 'download'] },
      granularity: fetchParams.granularity || 'monthly',
      genres: fetchParams.genres || [],
      platforms: ['All'],
      metrics: METRICS,
      regions,
      b_growth_ratio: true,
      b_group_by_date: true,
      b_group_by_market: true,
      start_date: fetchParams.start_date,
      end_date: fetchParams.end_date,
    };
  }, [selectedList, fetchParams, dataSource]);

  const getChartData = async () => {
    if (!updateParams) return;

    try {
      setChartLoading(true);
      const res = await getMobilePageData(updateParams);
      const rawData = res?.data?.metric_value_list || [];
      const formattedData = transformData({ input: rawData, countryMap });

      // console.debug('chart res', { rawData });
      setChartData(formattedData);
    } catch (err) {
      console.error('get chart data error:', err);
      setChartData(null);
    } finally {
      setChartLoading(false);
    }
  };

  useDeepCompareEffect(() => {
    if (updateParams) {
      getChartData();
    }
  }, [updateParams, countryMap]);

  return {
    chartType,
    setChartType,
    mainSegment,
    setMainSegment,
    chartLoading,
    chartData,
    setChartLoading,
  };
}
